// AdminSeriesDetailPage.tsx — (TF-6b-2; design §2, 22:00 answers) one series: the pilot (ordered with its estimate and the
// owner's cap first), Mark as posted (public, or it counts for nothing), traction per post and window (Pending until
// measured, never 0), pickup → the plan in words (written, approved) → the episodes ordered as one batch, each episode's
// state, run and posts, Return to planned, Drop (a confirmation) and Reopen (a reason). Re-planning an outcome goes
// through its run's "Needs you" card (the Run Inspector), never here. Every refusal is said in words.
// (B117) The audience: the profile every order carries; set or changed here (how a series made before B117 gets one).
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { PageContainer, TfCard, Btn } from "@/components/v2/primitives.tsx";
import { engineSeriesApi, type SeriesPatternView } from "@/api/engine-series.ts";
import { DryRunToggle } from "@/components/engine/DryRun.tsx";
import { tractionLines, postedWords, refusalText, episodeActions, episodeRunNote, orderButton, centsOf, rememberSeries,
  SERIES_REFUSAL_WORDS, audienceNoteOf, type SeriesView, type SeriesEpisodeView, type EstimateState } from "@/lib/series.ts";
import { audiencesApi, type ProfileRow } from "@/api/audiences.ts";
import { SeriesAudienceSelect } from "@/components/engine/SeriesAudience.tsx";

const storage = () => { try { return window.localStorage; } catch { return null; } };   // catch-reason: no storage → no history
const red = { color: "#b91c1c", fontSize: 13, marginTop: 6 } as const;
const runLink = (runId: string) => <Link to={`/admin/runs?run=${runId}`}>run {runId.slice(0, 8)}</Link>;

/** The pilot's or the batch's order: the estimate first, the owner's cap (pre-filled with its high end), then the button. */
function OrderBox({ id, kind, onDone, audienceNote }: { id: string; kind: "pilot" | "episodes"; onDone: () => void; audienceNote: string | null }) {
  const [est, setEst] = useState<EstimateState>({ state: "loading" });
  const [cap, setCap] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [dry, setDry] = useState(false);   // (tf3-35b) an admin's dry run: re-estimated (no clip priced), and ordered so
  useEffect(() => {
    let current = true;   // (session audit 10-17-B, B5) an answer for the toggle's previous setting never lands
    setEst({ state: "loading" });
    (kind === "pilot" ? engineSeriesApi.pilotEstimate(id, dry) : engineSeriesApi.episodesEstimate(id, dry))
      .then((r) => { if (!current) return; setEst({ state: "ready", ...r.estimate }); setCap((r.estimate.highCents / 100).toFixed(2)); })
      .catch((e) => { if (current) setEst({ state: "unavailable", text: refusalText(e) }); });   // catch-reason: the button stays off, with why
    return () => { current = false; };
  }, [id, kind, dry]);
  const capCents = centsOf(cap);
  const b = orderButton(est, capCents);
  const go = async () => {
    setBusy(true); setErr(null);
    try { await (kind === "pilot" ? engineSeriesApi.orderPilot(id, capCents!, dry) : engineSeriesApi.orderEpisodes(id, capCents!, dry)); onDone(); }
    catch (e) { setErr(refusalText(e)); }   // catch-reason: shown under the button; nothing was ordered
    finally { setBusy(false); }
  };
  return (
    <div style={{ marginTop: 8 }}>
      <DryRunToggle on={dry} onChange={setDry} />
      {est.state === "ready" && <label style={{ fontSize: 13 }}>Cap{kind === "episodes" ? " (each episode)" : ""} $
        <input value={cap} onChange={(e) => setCap(e.target.value)} style={{ width: 90, marginLeft: 4 }} /></label>}
      <div style={{ marginTop: 6 }}><Btn onClick={go} disabled={!b.enabled || busy}>{kind === "pilot" ? "Order the pilot" : "Order episodes"}</Btn></div>
      <div className="tf-muted" style={{ fontSize: 12, marginTop: 4 }}>{b.note}</div>
      {audienceNote && <div role="note" style={red}>{audienceNote}</div>}
      {err && <div style={red}>{err}</div>}
    </div>
  );
}

/** (B117) the series' audience: chosen from Admin → Audiences, saved on its own (PUT /:id/audience) */
function AudienceBox({ s, onDone }: { s: SeriesView; onDone: () => void }) {
  const [profiles, setProfiles] = useState<ProfileRow[] | null>(null);
  const [readErr, setReadErr] = useState<string | null>(null);
  const [value, setValue] = useState(s.audienceProfileId ?? "");
  // (audit 10-27) New series' "made, but its audience wasn't saved", carried here; and the box kept (not remounted) on a reload,
  //  so "Saved" stays, with the value following the series' own
  const madeErr = (useLocation().state as { audienceError?: string } | null)?.audienceError ?? null;
  useEffect(() => { setValue(s.audienceProfileId ?? ""); }, [s.audienceProfileId]);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => { audiencesApi.list().then((r) => setProfiles(r.profiles)).catch((e) => setReadErr(refusalText(e))); }, []);   // catch-reason: named under the select
  const save = async () => {
    setErr(null); setSaved(false);
    try { await engineSeriesApi.setAudience(s.id, value || null); setSaved(true); onDone(); }
    catch (e) { setErr(refusalText(e)); }   // catch-reason: shown under the select; nothing was changed
  };
  return (
    <div style={{ fontSize: 13 }}>
      <SeriesAudienceSelect value={value} onChange={(v) => { setValue(v); setSaved(false); }} profiles={profiles} style={{ minWidth: 320 }} />
      <Btn size="sm" onClick={save} disabled={value === (s.audienceProfileId ?? "")} style={{ marginLeft: 6 }}>Save the audience</Btn>
      {saved && <span className="tf-muted" style={{ marginLeft: 6 }}>Saved: the next order carries it.</span>}
      <div className="tf-muted" style={{ fontSize: 12, marginTop: 4 }}>Every pilot and episode order carries it (and its estimate). An order already made keeps the one it was made with.</div>
      {s.audienceError && <div style={red}>The series' audience couldn't be read: {s.audienceError}</div>}
      {madeErr && !s.audienceProfileId && <div style={red}>{madeErr}</div>}
      {readErr && <div style={red}>The audiences couldn't be read: {readErr}</div>}
      {err && <div style={red}>{err}</div>}
    </div>
  );
}

function MarkPosted({ s, ep, onDone }: { s: SeriesView; ep: SeriesEpisodeView; onDone: () => void }) {
  const [url, setUrl] = useState("");
  const [platform, setPlatform] = useState(s.primaryPlatform);
  const [said, setSaid] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const go = async () => {
    setErr(null); setSaid(null);
    try { setSaid(postedWords(await engineSeriesApi.markPosted(s.id, ep.seq, { platform, url: url.trim() }))); onDone(); }
    catch (e) { setErr(refusalText(e)); }   // catch-reason: shown beside the form; nothing was recorded
  };
  return (
    <div style={{ marginTop: 6, fontSize: 13 }}>
      <input placeholder="The post's link" value={url} onChange={(e) => setUrl(e.target.value)} style={{ width: 280 }} />
      <select value={platform} onChange={(e) => setPlatform(e.target.value)} style={{ marginLeft: 4 }}>
        <option value="tiktok">TikTok</option><option value="youtube">YouTube</option></select>
      <Btn size="sm" onClick={go} disabled={!url.trim()} style={{ marginLeft: 6 }}>Mark as posted</Btn>
      {said && <div style={{ marginTop: 4 }}>{said}</div>}
      {err && <div style={red}>{err}</div>}
    </div>
  );
}

function ReasonAction({ label, onSubmit }: { label: string; onSubmit: (reason: string) => Promise<unknown> }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  if (!open) return <Btn size="sm" variant="ghost" onClick={() => setOpen(true)}>{label}</Btn>;
  return (
    <span style={{ fontSize: 13 }}>
      <input placeholder="Why (3–300 characters)" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} style={{ width: 240 }} />
      <Btn size="sm" disabled={reason.trim().length < 3} onClick={async () => { setErr(null);
        try { await onSubmit(reason.trim()); setOpen(false); } catch (e) { setErr(refusalText(e)); } }}   /* catch-reason: shown beside it */
        style={{ marginLeft: 6 }}>{label}</Btn>
      {err && <div style={red}>{err}</div>}
    </span>
  );
}

function PlanEditor({ s, p, onDone }: { s: SeriesView; p: SeriesPatternView | undefined; onDone: () => void }) {
  const picked = s.episodes.filter((e) => e.seq > 0 && e.status === "planned");
  const [rows, setRows] = useState(() => picked.map((e) => ({ seq: e.seq, outcome: e.planned?.outcome ?? "",
    stateAfter: Object.fromEntries((p?.state ?? []).map((d) => [d.key, String(e.planned?.stateAfter?.[d.key] ?? "")])) })));
  const [ends, setEnds] = useState(!!s.plan?.endsSeries);
  const [err, setErr] = useState<string | null>(null);
  const plan = s.plan ?? { version: 0, approvedVersion: null, approvedAt: null, approvedBy: null, endsSeries: false };
  const approved = plan.version > 0 && plan.approvedVersion === plan.version;
  const save = async () => {
    setErr(null);
    try {
      await engineSeriesApi.putPlan(s.id, { endsSeries: ends, episodes: rows.map((r) => ({ seq: r.seq, outcome: r.outcome.trim(),
        stateAfter: Object.fromEntries(Object.entries(r.stateAfter).filter(([, v]) => v.trim() !== "").map(([k, v]) =>
          [k, p?.state.find((d) => d.key === k)?.type === "integer" ? Number(v) : v.trim()])) })) });
      onDone();
    } catch (e) { setErr(refusalText(e)); }   // catch-reason: shown under the plan; nothing was written
  };
  if (!picked.length) return <div className="tf-muted" style={{ fontSize: 13 }}>No picked-up episode is waiting for a plan.</div>;
  return (
    <div style={{ fontSize: 13 }}>
      <div className="tf-muted">Plan version {plan.version || "none"}{approved ? `, approved by ${plan.approvedBy}` : plan.version ? ", not approved" : ""}. Words only: nothing is made until it is approved.</div>
      {rows.map((r, i) => (
        <div key={r.seq} style={{ marginTop: 8 }}>
          Episode {r.seq}: <input value={r.outcome} placeholder="What happens (3–300 characters)" style={{ width: 340 }}
            onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, outcome: e.target.value } : x)))} />
          {(p?.state ?? []).map((d) => (
            <label key={d.key} style={{ marginLeft: 8 }} title={d.meaning}>{d.key} <input value={r.stateAfter[d.key] ?? ""} style={{ width: 60 }}
              onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, stateAfter: { ...x.stateAfter, [d.key]: e.target.value } } : x)))} /></label>))}
        </div>))}
      <label style={{ display: "block", marginTop: 8 }}><input type="checkbox" checked={ends} onChange={(e) => setEnds(e.target.checked)} /> The last episode is the finale (the series ends)</label>
      <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
        <Btn size="sm" onClick={save} disabled={rows.some((r) => r.outcome.trim().length < 3)}>Save the plan</Btn>
        {plan.version > 0 && !approved && <Btn size="sm" onClick={async () => { setErr(null);
          try { await engineSeriesApi.approvePlan(s.id); onDone(); } catch (e) { setErr(refusalText(e)); } }}>Approve plan version {plan.version}</Btn>}
      </div>
      {err && <div style={red}>{err}</div>}
    </div>
  );
}

export function AdminSeriesDetailPage() {
  const { id = "" } = useParams();
  const [s, setS] = useState<SeriesView | null>(null);
  const [patterns, setPatterns] = useState<SeriesPatternView[]>([]);
  const [readErr, setReadErr] = useState<string | null>(null);
  const [actErr, setActErr] = useState<string | null>(null);
  const [from, setFrom] = useState("1");
  const [to, setTo] = useState("3");
  const load = useCallback(() => {
    engineSeriesApi.get(id).then((r) => { setS(r); setReadErr(null); rememberSeries(storage(), { id: r.id, title: r.title }); })
      .catch((e) => setReadErr(refusalText(e)));   // catch-reason: shown in place of the series
  }, [id]);
  useEffect(() => { load(); engineSeriesApi.patterns().then((r) => setPatterns(r.patterns)).catch(() => setPatterns([])); }, [load]);   // catch-reason: no state keys to edit; the plan form says so by showing none
  const act = async (f: () => Promise<unknown>) => { setActErr(null); try { await f(); load(); } catch (e) { setActErr(refusalText(e)); } };   // catch-reason: shown at the top
  if (readErr) return <PageContainer title="Series"><div style={red}>The series couldn't be read: {readErr}</div></PageContainer>;
  if (!s) return <PageContainer title="Series"><div className="tf-muted">Reading…</div></PageContainer>;
  const p = patterns.find((x) => x.id === s.patternId);
  const pilot = s.episodes.find((e) => e.seq === 0);
  const active = s.status === "pilot" || s.status === "picked_up";
  const plan = s.plan;
  const approved = !!plan && plan.version > 0 && plan.approvedVersion === plan.version;
  const toOrder = s.episodes.some((e) => e.seq > 0 && e.status === "planned");
  const live = s.episodes.filter((e) => e.status === "planned" || e.status === "ordered").length;
  return (
    <PageContainer title={s.title} subtitle={`${p?.name ?? s.patternId} · ${s.status} · main platform ${s.primaryPlatform}`}
      action={<Link to="/admin/series"><Btn variant="ghost">All series</Btn></Link>}>
      <div className="tf-muted" style={{ marginBottom: 12 }}>{s.premise}</div>
      {actErr && <div style={red}>{actErr}</div>}

      <TfCard>
        <h3 style={{ marginTop: 0 }}>The audience</h3>
        <AudienceBox s={s} onDone={load} />
      </TfCard>

      <TfCard>
        <h3 style={{ marginTop: 0 }}>The pilot</h3>
        {pilot ? <div style={{ fontSize: 14 }}>Episode 0: {pilot.status}{pilot.runId && <> · {runLink(pilot.runId)}</>}</div> : <div className="tf-muted">No pilot.</div>}
        {pilot?.status === "planned" && s.status === "pilot" && <OrderBox id={s.id} kind="pilot" onDone={load} audienceNote={audienceNoteOf(s)} />}
      </TfCard>

      <TfCard>
        <h3 style={{ marginTop: 0 }}>Episodes</h3>
        {s.episodes.map((ep) => { const a = episodeActions(ep, s); return (
          <div key={ep.seq} style={{ borderTop: "1px solid var(--tf-border, #e5e7eb)", padding: "8px 0", fontSize: 14 }}>
            <strong>Episode {ep.seq}</strong> · {ep.status}{a.openRun && ep.runId && <> · {runLink(ep.runId)}</>}
            {ep.planned?.outcome && <div className="tf-muted" style={{ fontSize: 13 }}>Planned: {ep.planned.outcome}</div>}
            {ep.posts.map((x, i) => (
              <div key={i} style={{ fontSize: 13, marginTop: 4 }}>
                {x.platform}: {x.permalink ? <a href={x.permalink} target="_blank" rel="noreferrer">{x.permalink}</a> : x.platformRef}
                {" · "}{x.visibility === "public" ? "public" : x.visibility === "private" ? "not public, so it doesn't count" : "couldn't tell whether it is public"}
                {x.visibilityReason ? ` (${x.visibilityReason})` : ""}
                <ul style={{ margin: "2px 0 0", paddingLeft: 18 }}>{tractionLines(x.traction).map((l) => <li key={l}>{l}</li>)}</ul>
              </div>))}
            {a.markPosted && <MarkPosted s={s} ep={ep} onDone={load} />}
            {episodeRunNote(ep, s) && <div role="note" className="tf-muted" style={{ fontSize: 13, marginTop: 6 }}>{episodeRunNote(ep, s)}</div>}
            {a.returnToPlanned && <div style={{ marginTop: 6 }}><ReasonAction label="Return to planned" onSubmit={(r) => engineSeriesApi.returnEpisode(s.id, ep.seq, r).then(load)} />
              <span className="tf-muted" style={{ fontSize: 12, marginLeft: 6 }}>Only when its run failed or was cancelled.</span></div>}
          </div>); })}
      </TfCard>

      {active && <TfCard>
        <h3 style={{ marginTop: 0 }}>Pick up episodes</h3>
        <div style={{ fontSize: 13 }}>Episodes <input value={from} onChange={(e) => setFrom(e.target.value)} style={{ width: 50 }} /> to{" "}
          <input value={to} onChange={(e) => setTo(e.target.value)} style={{ width: 50 }} />{" "}
          <Btn size="sm" onClick={() => act(() => engineSeriesApi.pickup(s.id, Number(from), Number(to)))}>Pick up episodes {from}–{to}</Btn></div>
        <div className="tf-muted" style={{ fontSize: 12, marginTop: 4 }}>Once the pilot is posted (public). Then write the plan and approve it.</div>
      </TfCard>}

      {s.status === "picked_up" && <TfCard>
        <h3 style={{ marginTop: 0 }}>The plan</h3>
        <PlanEditor key={`${plan?.version}-${s.episodes.length}`} s={s} p={p} onDone={load} />
        {approved && toOrder && <OrderBox id={s.id} kind="episodes" onDone={load} audienceNote={audienceNoteOf(s)} />}
        {!approved && toOrder && <div className="tf-muted" style={{ fontSize: 12, marginTop: 8 }}>{SERIES_REFUSAL_WORDS.PLAN_NOT_APPROVED}</div>}
      </TfCard>}

      <TfCard>
        {s.status !== "dropped" && s.status !== "ended" && <Btn variant="ghost" onClick={() => {
          if (window.confirm(`Drop this series? ${live} planned or ordered episode${live === 1 ? "" : "s"} stop.`)) act(() => engineSeriesApi.drop(s.id)); }}>Drop this series</Btn>}
        {s.status === "ended" && <ReasonAction label="Reopen" onSubmit={(r) => engineSeriesApi.reopen(s.id, r).then(load)} />}
        {s.state && s.state.length > 0 && <div className="tf-muted" style={{ fontSize: 12, marginTop: 8 }}>State: {s.state.map((x) => `${x.key} ${x.value} (after episode ${x.asOfSeq})`).join(" · ")}</div>}
        <div className="tf-muted" style={{ fontSize: 12, marginTop: 8 }}>An outcome is re-planned from its run's "Needs you" card, in the run's page.</div>
      </TfCard>
    </PageContainer>
  );
}
